import { useEffect, useCallback } from 'react'
import {
  CreateUnitTabs,
  FileUploadStatus,
  StructureGenerationStatus,
  ContentGenerationStatus
} from '../contexts/CorporateContext'
import { useAuth } from '@hooks/Authentication'
import { Project } from '../types/project'
import { CreateUnitPrefillSnapshot } from '../types/createUnitPrefill'

export type GenerationStatus =
  | 'GENERATE_CORPORATE_STRUCTURE'
  | 'GENERATE_CONTENT'

interface DocumentProcessingData {
  jobId: string
  status: FileUploadStatus
  projectId: number
  type: 'PROCESS_DOCUMENT'
}

interface GenerationData {
  generationId: number
  projectId: number
  status: StructureGenerationStatus
  type: GenerationStatus
}

export interface CreateUnitPrefillEventData {
  type: 'CREATE_UNIT_FROM_DESCRIPTION'
  jobId: string
  projectId: number
  status: 'PROCESSING' | 'COMPLETED' | 'ENDED'
  step: CreateUnitTabs | 'orchestration'
  snapshot: CreateUnitPrefillSnapshot
}

interface UseSocketManagerProps {
  project: Project | null
  prefillJobId?: string | null
  setFileUploadStatus: (status: FileUploadStatus) => void
  setStructureGenerationStatus: (status: StructureGenerationStatus) => void
  setContentGenerationStatus: (status: ContentGenerationStatus) => void
  fetchStructure: () => Promise<void>
  fetchContent: () => Promise<void>
  onCreateUnitPrefillEvent?: (event: CreateUnitPrefillEventData) => void
}

export const useSocketManager = ({
  project,
  prefillJobId,
  setFileUploadStatus,
  setStructureGenerationStatus,
  setContentGenerationStatus,
  fetchStructure,
  fetchContent,
  onCreateUnitPrefillEvent
}: UseSocketManagerProps) => {
  const { socket } = useAuth()

  const onDocumentProcessingEvent = useCallback(
    (eventData: { data: string }) => {
      try {
        const data = eventData.data
        const parsedData: DocumentProcessingData =
          typeof data === 'string' ? JSON.parse(data) : data

        const {
          status: newStatus,
          projectId: eventProjectId,
          type
        } = parsedData

        if (eventProjectId && project?.id && eventProjectId !== project?.id) {
          return
        }

        if (type !== 'PROCESS_DOCUMENT') {
          console.warn('Unknown document processing type:', type)
          return
        }

        setFileUploadStatus(newStatus)
      } catch (error) {
        console.error(
          'Error parsing DOCUMENT_PROCESSING event data:',
          error,
          eventData
        )
      }
    },
    [project, setFileUploadStatus]
  )

  const onGenerationEvent = useCallback(
    (eventData: { data: string }) => {
      const data = eventData.data
      const parsedData: GenerationData =
        typeof data === 'string' ? JSON.parse(data) : data

      const { projectId: eventProjectId, status, type } = parsedData

      if (eventProjectId && project?.id && eventProjectId !== project?.id) {
        return
      }

      if (type === 'GENERATE_CORPORATE_STRUCTURE') {
        if (status === 'COMPLETED') {
          fetchStructure()
        }
        setStructureGenerationStatus(status)
      }
      if (type === 'GENERATE_CONTENT') {
        if (status === 'COMPLETED') {
          fetchContent()
        }
        setContentGenerationStatus(status)
      }
    },
    [
      project,
      setStructureGenerationStatus,
      setContentGenerationStatus,
      fetchStructure,
      fetchContent
    ]
  )

  const onCreateUnitPrefillSocketEvent = useCallback(
    (eventData: { data: string }) => {
      if (!onCreateUnitPrefillEvent) {
        return
      }

      try {
        const data = eventData.data
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data

        const event: CreateUnitPrefillEventData = parsedData.type
          ? parsedData
          : parsedData.data
          ? typeof parsedData.data === 'string'
            ? JSON.parse(parsedData.data)
            : parsedData.data
          : parsedData

        if (event.type !== 'CREATE_UNIT_FROM_DESCRIPTION') {
          return
        }

        if (!prefillJobId || event.jobId !== prefillJobId) {
          return
        }

        if (project?.id && event.projectId !== project.id) {
          return
        }

        onCreateUnitPrefillEvent(event)
      } catch (error) {
        console.error(
          'Error parsing CREATE_UNIT_PREFILL event data:',
          error,
          eventData
        )
      }
    },
    [project, prefillJobId, onCreateUnitPrefillEvent]
  )

  const onConnect = useCallback(() => {
    if (socket) {
      socket.emit('join_room', 'mavis')
    }
  }, [socket])

  useEffect(() => {
    if (socket) {
      if (socket.connected) {
        socket.emit('join_room', 'mavis')
      }

      socket.on('connect', onConnect)
      socket.on('DOCUMENT_PROCESSING', onDocumentProcessingEvent)
      socket.on('GENERATION', onGenerationEvent)
      socket.on('CREATE_UNIT_PREFILL', onCreateUnitPrefillSocketEvent)

      /* socket.onAny((eventName: any, ...args: any) => {
        console.log(
          'Received socket event from mavis room =====',
          eventName,
          args
        )
      }) */

      return () => {
        socket.off('connect', onConnect)
        socket.off('DOCUMENT_PROCESSING', onDocumentProcessingEvent)
        socket.off('GENERATION', onGenerationEvent)
        socket.off('CREATE_UNIT_PREFILL', onCreateUnitPrefillSocketEvent)
        socket.emit('leave_room', 'mavis')
      }
    }
  }, [
    socket,
    onDocumentProcessingEvent,
    onConnect,
    onGenerationEvent,
    onCreateUnitPrefillSocketEvent
  ])
}
